import { DateTime } from 'luxon';
import _ from 'lodash';
import { entityStore, sirenActions, useSirenEntity } from '~/utility/BinSentry-ui-utility';
import { useCallback } from 'react';

function buildForecastOrderHref({ statsEntity, orderId, orderVolume, orderDate }) {
  let orderDateTime = orderDate ? DateTime.fromISO(orderDate) : undefined;
  // assume the order happens at the beginning of a 4 hour interval
  orderDateTime = orderDateTime?.minus({ hours: orderDateTime.hour % 4, milliseconds: 1 });
  const forecastOrderAction = statsEntity?.getAction('order-forecast');
  return forecastOrderAction && sirenActions.parseAction(forecastOrderAction, {
    orderVolume,
    orderDate: (orderVolume || orderId) ? orderDateTime?.toUTC().toISO() : null,
    orderId,
  })?.url?.href;
}

export function useOrderForecast(statsEntity, orderId, orderVolume, orderDate) {
  const forecastOrderHref = buildForecastOrderHref({ statsEntity, orderId, orderVolume, orderDate });
  const { entity, loading } = useSirenEntity({ href: forecastOrderHref });
  const volumes = entity?.properties?.volumes || [];
  const volumeMap = new Map();
  volumes.forEach(v => {
    const dateTime = DateTime.fromISO(v.publishedAt).toLocal();
    const date = dateTime.toISODate();
    const hour = dateTime.hour;
    const day = volumeMap.get(date) || {};
    day[Math.floor(hour / 4)] = v;
    volumeMap.set(date, day);
  });
  return { entity, volumeMap, loading };
}

export function useMixedStatsOrderForecast({ binId, orderDate, orderVolume = 0, orderId }) {
  const { entity: binEntity, loading: loadingBin } = useSirenEntity({ href: binId });
  const statsHref = binEntity?.getSubEntity('https://api.binsentry.com/rel/bin-stat-latest')?.href;
  const { entity: statsEntity, loading: loadingStats } = useSirenEntity({ href: statsHref });
  const forecastOrderHref = buildForecastOrderHref({ statsEntity, orderId, orderVolume, orderDate });
  const { entity: forecastEntity, loading: loadingOrderForecast } = useSirenEntity({ href: forecastOrderHref });
  const refresh = useCallback(async () => {
    await entityStore.invalidateBy(({ href }) => {
      return href.includes(binId) && href.includes('order-forecast');
    });
  }, [binId]);
  const loading = loadingStats || loadingOrderForecast || loadingBin;
  if (loading) {
    return { loading: true };
  }

  const orderForecast = forecastEntity?.properties.volumes || [];
  const statForecast = statsEntity?.properties.volume.forecast || [];

  const mixedForecast = [
    ...statForecast.map(forecastedLevel => {
      const orderForecastedLevel = orderForecast.find(orderForecastedLevel => orderForecastedLevel.publishedAt === forecastedLevel.publishedAt);
      return {
        isDelivery: orderForecastedLevel?.isDelivery || false,
        publishedAt: forecastedLevel.publishedAt,
        low: forecastedLevel.low + (orderForecastedLevel?.volume || 0),
      };
    }),
    ...orderForecast.filter(orderForecastedLevel => orderForecastedLevel.publishedAt > _.last(statForecast)?.publishedAt).map(orderForecastedLevel => ({
      isDelivery: orderForecastedLevel.isDelivery,
      publishedAt: orderForecastedLevel.publishedAt,
      low: orderForecastedLevel.volume,
    })),
  ];

  // Only show the emptyAt if there are no delivery forecasts
  const emptyAtWithOrders = orderForecast.find(orderForecast => orderForecast.isDelivery)
    ? mixedForecast.find(entry => entry.low <= 0)?.publishedAt || null
    : statsEntity?.properties.emptyAt;

  return {
    mixedForecast,
    emptyAtWithOrders,
    loading,
    refresh,
  };
}
